"use client";

import React, { useState } from "react";
import Image from "next/image";
import Banner from '@/components/ui/banner';
import { useTranslations } from "next-intl";
import { truncateText } from "@/utils/text";
import {
  Mail,
  Building2,
  Wrench,
  Shield,
  TrendingUp,
} from "lucide-react";
import { Link } from "@/i18n/navigation";
import { Button, buttonVariants } from "@/components/ui/button";
import { WobbleCard } from "@/components/ui/wobble-card";
import { useServiceMenuItems, type ServiceMenuItem } from "@/hooks/useServiceMenuItems";
import { getServicePath } from "@/lib/service-slug";
import { renderWithStrong } from "@/lib/utils";

type SeoSection = {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  paragraphs: React.ReactNode[];
};

type SeoSectionCopy = {
  title: string;
  paragraphs: string[];
};

const CARD_ACCENT_COLORS = ["#0c3b52", "#D01B17", "#46B1C9", "#f59e0b", "#2f7c87", "#623CEA"];

function ServiceCard({
  item,
  accentColor,
  altOptions,
}: {
  item: ServiceMenuItem;
  accentColor: string;
  altOptions: string[];
}) {
  const tServices = useTranslations("services");
  const serviceHref = getServicePath(item.slug);

  const imageAlt = (() => {
    const options = altOptions?.length > 0
      ? altOptions
      : ["empresa placas solares", "placas solares", "paneles solares"];
    let hash = 0;
    for (let i = 0; i < item.slug.length; i += 1) {
      hash = (hash * 31 + item.slug.charCodeAt(i)) % 100000;
    }
    return options[Math.abs(hash) % options.length];
  })();

  return (
    <Link href={serviceHref} className="block h-full">
      <WobbleCard
        containerClassName="group h-full bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70 transition-all duration-500 hover:shadow-2xl"
        className="h-full px-0 py-0"
      >
        <div className="relative px-6 pt-6">
          <div className="relative h-48 w-full overflow-hidden rounded-2xl shadow-card transition-transform duration-500 group-hover:-translate-y-0.5">
            <Image
              src={item.image}
              alt={imageAlt}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(min-width: 1024px) 50vw, 100vw"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          </div>
        </div>

        <div className="px-6 pb-8 pt-6">
          <h3
            className="text-2xl font-bold text-gray-900 dark:text-white mb-4 transition-colors duration-300 group-hover:opacity-90"
            onMouseEnter={(e) => {
              e.currentTarget.style.color = accentColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "";
            }}
          >
            {item.title}
          </h3>

          <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed text-lg">
            {truncateText(item.description, 180)}
          </p>

          <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-red dark:text-brand-red-soft">
            {tServices("learnMore")}
            <span aria-hidden="true">→</span>
          </div>
        </div>
      </WobbleCard>
    </Link>
  );
}

const ServicesPage = () => {
  const tServices = useTranslations("services");
  const tPage = useTranslations("servicesPage");
  const items = useServiceMenuItems();
  const [expandedSeoSections, setExpandedSeoSections] = useState<Record<string, boolean>>({});
  const cardAltOptions = tPage.raw("cardAltOptions") as string[];
  const seoCopy = tPage.raw("seo.sections") as SeoSectionCopy[];
  const seoIcons = [Building2, Wrench, Shield];
  const seoSections: SeoSection[] = seoCopy.map((section, sectionIndex) => ({
    title: section.title,
    icon: seoIcons[sectionIndex] ?? Building2,
    paragraphs: section.paragraphs.map((paragraph) => renderWithStrong(paragraph)),
  }));

  const toggleSeoSection = (title: string) => {
    setExpandedSeoSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F3F7FB] via-white to-[#F9FAFB] dark:from-[#121826] dark:via-[#141a2c] dark:to-dark text-gray-800 dark:text-white transition-colors duration-300">
      {/* Banner Section (generalized) */}
      <Banner
        title={tPage("hero.title")}
        subtitle={tPage("hero.intro")}
        backgroundAlt={tPage("hero.backgroundAlt")}
        backgroundImage={'/static/backgrounds/services-background.webp'}
      />

      {/* Services Grid */}
      <section className="section-y px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {items.map((item, index) => (
              <ServiceCard
                key={item.slug}
                item={item}
                accentColor={CARD_ACCENT_COLORS[index % CARD_ACCENT_COLORS.length]}
                altOptions={cardAltOptions}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {seoSections.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSeoSections[section.title] ?? false;
              return (
                <article
                  key={section.title}
                  className="self-start rounded-card bg-white/90 dark:bg-gray-900/70 border border-brand-navy/10 dark:border-white/10 p-8 shadow-card transition-all duration-300"
                >
                  <div className="mb-6 flex items-start gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand-navy/10 dark:bg-white/10">
                      <Icon className="h-8 w-8 text-brand-red" />
                    </div>
                    <h2 className="text-brand-navy dark:text-[#7ED4E6] type-h2">
                      {section.title}
                    </h2>
                  </div>
                  <div
                    className={`relative space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed text-base transition-[max-height] duration-300 ${
                      isExpanded ? "max-h-[1400px]" : "max-h-[260px] overflow-hidden"
                    }`}
                  >
                    {section.paragraphs.map((paragraph, index) => (
                      <p key={`${section.title}-${index}`}>{paragraph}</p>
                    ))}
                    {!isExpanded && (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/95 to-transparent dark:from-gray-900/95" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleSeoSection(section.title)}
                    className={buttonVariants({ variant: "pillOutline", size: "pillSm", className: "mt-5 inline-flex" })}
                  >
                    {isExpanded ? tPage("seo.readLess") : tPage("seo.readMore")}
                  </button>
                </article>
              );
            })}
          </div>

          <article className="mt-10 rounded-card bg-gradient-to-r from-brand-navy via-brand-navy-light to-brand-navy text-white p-8 md:p-10 shadow-card-lg">
            <div className="mb-5 flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 p-3">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold leading-tight">
                {tPage("benefits.title")}
              </h3>
            </div>
            <p className="text-lg leading-relaxed text-white/95">
              {renderWithStrong(tPage("benefits.paragraph"))}
            </p>
          </article>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-brand-navy via-brand-navy-light to-brand-navy text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl md:text-5xl font-bold mb-6">
            {tServices("cta.title")}
          </h3>
          <p className="text-xl mb-8 opacity-90">
            {tServices("cta.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contacto">
              <Button
              className="bg-white text-brand-navy hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-3 text-lg font-semibold"
              >
              <Mail className="w-5 h-5 mr-2" />
              {tServices("cta.contact")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ServicesPage;
